/* ============================================================
   DATA JADWAL DOKTER

   Letakkan di: app/jadwal-dokter/dokter.js

   ============================================================
   ⚠️⚠️ DAFTAR DOKTER SENGAJA KOSONG

   Nama dokter, spesialisasinya, dan jam praktiknya TIDAK BOLEH
   dikarang - bahkan sebagai contoh sementara. Halaman jadwal dokter
   yang berisi nama palsu akan membuat orang datang menemui dokter
   yang tidak ada, atau pada jam yang tidak dilayani.

   Yang sudah jadi: seluruh mesin halamannya - pencarian nama, tapis
   spesialisasi, tapis hari, kartu dokter, dan tombol pendaftaran.
   Begitu DOKTER di bawah diisi, semuanya hidup sendiri.

   ============================================================
   BENTUK DATA

   {
     id: "budi-santoso",              // huruf kecil, pemisah strip.
                                      // Dipakai sebagai kunci React,
                                      // jadi HARUS unik.
     nama: "dr. Budi Santoso, Sp.PD",
     spesialisasi: { id: "Penyakit Dalam", en: "Internal Medicine" },
     foto: null,                      // lihat catatan FOTO di bawah
     jadwal: [
       { hari: 1, mulai: "08:00", selesai: "12:00" },
       { hari: 3, mulai: "14:00", selesai: "17:00" },
     ],
   }

   `hari`: 0 = Minggu, 1 = Senin, ... 6 = Sabtu.

   Satu dokter boleh punya beberapa baris di hari yang sama - misalnya
   praktik pagi dan sore. Tulis dua baris, jangan digabung.

   ============================================================
   FOTO

   Taruh di app/jadwal-dokter/images/, lalu impor di atas berkas ini:

       import fotoBudi from "./images/budi-santoso.jpg";

   dan pakai `foto: fotoBudi`. Impor statis, BUKAN string path -
   berkas di dalam app/ tidak disajikan sebagai URL.

   Dokter tanpa foto TIDAK kosong di halaman: kartunya menampilkan
   inisial namanya di atas bidang berwarna. Itu disengaja - foto
   kosong berbingkai abu-abu terlihat seperti gambar yang gagal
   dimuat, sedangkan inisial terlihat sebagai pilihan.

   ⚠️ Minta izin tiap dokter sebelum memasang fotonya. Foto orang
   bukan aset rumah sakit.

   ============================================================
   YANG PERLU DIMINTA KE BAGIAN PELAYANAN MEDIS

     1. daftar dokter beserta gelar lengkap dan spesialisasinya
     2. jam praktik tiap dokter per hari
     3. apakah ada dokter yang praktiknya bergantian minggu ganjil
        dan genap - kalau ada, bentuk data ini perlu ditambah dan
        beri tahu saya
   ============================================================ */

/* Daftar dokter yang sebenarnya. INI yang diisi. */
const DOKTER_ASLI = [];

/* ============================================================
   DATA CONTOH - 10 dokter karangan

   ⚠️⚠️ SEMUA NAMA DI BAWAH KARANGAN. Tidak ada satu pun dokter
   bernama begini di RS JMC. Gunanya hanya untuk melihat tampilan
   halaman bekerja: pencarian, tapis spesialisasi, tapis hari, dan
   kartu dokter.

   Setiap nama diawali kata "Contoh" supaya tidak mungkin tertukar
   dengan dokter sungguhan - baik olehmu, maupun oleh orang lain yang
   membuka berkas ini enam bulan lagi.

   PENGAMANNYA ADA DI BARIS PALING BAWAH BERKAS INI: data contoh
   hanya dipakai saat pengembangan. Begitu situs dibangun untuk
   tayang (npm run build), Next.js otomatis memakai DOKTER_ASLI -
   yang sekarang masih kosong, sehingga halaman menampilkan "Jadwal
   dokter sedang disiapkan".

   Jadi nama-nama karangan ini TIDAK BISA sampai ke pengunjung,
   bahkan kalau kamu lupa mencabutnya.

   CARA MEMATIKANNYA saat mengembangkan: ubah PAKAI_CONTOH jadi false.
   ============================================================ */
const PAKAI_CONTOH = true;

const DOKTER_CONTOH = [
  {
    id: "contoh-adiyatma",
    nama: "dr. Contoh Adiyatma, Sp.PD",
    spesialisasi: { id: "Penyakit Dalam", en: "Internal Medicine" },
    foto: null,
    jadwal: [
      { hari: 1, mulai: "08:00", selesai: "12:00" },
      { hari: 3, mulai: "08:00", selesai: "12:00" },
      { hari: 5, mulai: "14:00", selesai: "17:00" },
    ],
  },
  {
    id: "contoh-bramantyo",
    nama: "dr. Contoh Bramantyo, Sp.A",
    spesialisasi: { id: "Anak", en: "Paediatrics" },
    foto: null,
    /* Dua sesi di hari yang sama - pagi dan sore. Ditulis dua baris,
       bukan digabung, supaya jeda istirahatnya terlihat. */
    jadwal: [
      { hari: 2, mulai: "09:00", selesai: "12:00" },
      { hari: 2, mulai: "16:00", selesai: "19:00" },
      { hari: 4, mulai: "09:00", selesai: "12:00" },
    ],
  },
  {
    id: "contoh-cempaka",
    nama: "dr. Contoh Cempaka, Sp.OG",
    spesialisasi: { id: "Obstetri & Ginekologi", en: "Obstetrics & Gynaecology" },
    foto: null,
    jadwal: [
      { hari: 1, mulai: "14:00", selesai: "18:00" },
      { hari: 4, mulai: "14:00", selesai: "18:00" },
      { hari: 6, mulai: "09:00", selesai: "12:00" },
    ],
  },
  {
    id: "contoh-darmawan",
    nama: "dr. Contoh Darmawan, Sp.B",
    spesialisasi: { id: "Bedah Umum", en: "General Surgery" },
    foto: null,
    jadwal: [
      { hari: 2, mulai: "13:00", selesai: "16:00" },
      { hari: 5, mulai: "13:00", selesai: "16:00" },
    ],
  },
  {
    id: "contoh-erlangga",
    nama: "dr. Contoh Erlangga, Sp.JP",
    spesialisasi: { id: "Jantung & Pembuluh Darah", en: "Cardiology" },
    foto: null,
    jadwal: [
      { hari: 1, mulai: "08:00", selesai: "11:00" },
      { hari: 3, mulai: "16:00", selesai: "19:00" },
    ],
  },
  {
    id: "contoh-fatimah",
    nama: "dr. Contoh Fatimah, Sp.M",
    spesialisasi: { id: "Mata", en: "Ophthalmology" },
    foto: null,
    jadwal: [
      { hari: 2, mulai: "08:00", selesai: "12:00" },
      { hari: 4, mulai: "08:00", selesai: "12:00" },
    ],
  },
  {
    id: "contoh-gunawan",
    nama: "dr. Contoh Gunawan, Sp.THT-KL",
    spesialisasi: { id: "THT - Kepala Leher", en: "ENT - Head & Neck" },
    foto: null,
    jadwal: [
      { hari: 3, mulai: "13:00", selesai: "16:00" },
      { hari: 5, mulai: "13:00", selesai: "16:00" },
    ],
  },
  {
    id: "contoh-hanindita",
    nama: "dr. Contoh Hanindita, Sp.KK",
    spesialisasi: { id: "Kulit & Kelamin", en: "Dermatology" },
    foto: null,
    /* Hanya satu hari - untuk menguji kartu berjadwal pendek. */
    jadwal: [{ hari: 6, mulai: "08:00", selesai: "11:00" }],
  },
  {
    id: "contoh-iskandar",
    nama: "dr. Contoh Iskandar, Sp.S",
    spesialisasi: { id: "Saraf", en: "Neurology" },
    foto: null,
    jadwal: [
      { hari: 1, mulai: "16:00", selesai: "19:00" },
      { hari: 4, mulai: "16:00", selesai: "19:00" },
    ],
  },
  {
    id: "contoh-jayanti",
    nama: "drg. Contoh Jayanti, Sp.KG",
    spesialisasi: { id: "Gigi - Konservasi", en: "Dentistry - Conservative" },
    foto: null,
    jadwal: [
      { hari: 2, mulai: "10:00", selesai: "14:00" },
      { hari: 5, mulai: "10:00", selesai: "14:00" },
    ],
  },
];

/* ============================================================
   PENGAMAN

   `process.env.NODE_ENV` bernilai "production" saat `npm run build`.
   Jadi data contoh hanya hidup di komputermu, dan tidak pernah ikut
   ke situs yang tayang - meski PAKAI_CONTOH lupa dimatikan.
   ============================================================ */
const BOLEH_CONTOH = PAKAI_CONTOH && process.env.NODE_ENV !== "production";

export const MEMAKAI_CONTOH = BOLEH_CONTOH;
export const DOKTER = BOLEH_CONTOH ? DOKTER_CONTOH : DOKTER_ASLI;

/* ============================================================
   NOMOR PENDAFTARAN

   Dibaca dari spanduk halaman jadwal dokter yang sekarang. Dua jalur
   pendaftaran dibedakan karena antreannya memang berbeda, dan pasien
   BPJS yang menelepon nomor pribadi hanya akan dialihkan.
   ============================================================ */
export const PENDAFTARAN = [
  {
    kunci: "pribadi",
    label: { id: "Pribadi / Asuransi", en: "Private / Insurance" },
    tampil: "0811 901 874",
    tel: "+62811901874",
    wa: "62811901874",
  },
  {
    kunci: "bpjs",
    label: { id: "BPJS / JKN", en: "BPJS / JKN" },
    tampil: "0812 1208 777",
    tel: "+628121208777",
    wa: "628121208777",
  },
];

/* Nomor pengaduan, dari spanduk yang sama. Ditampilkan kecil di
   bagian bawah - bukan disembunyikan, tapi juga tidak bersaing
   dengan nomor pendaftaran. */
export const KOMPLAIN = [
  { tampil: "0815 801 7000", tel: "+62815801700", wa: "628158017000" },
];

/* ============================================================
   PESAN PEMBUKA WHATSAPP

   {nama} dan {hari} diganti otomatis dengan dokter dan hari yang
   sedang dipilih pengunjung. Kalau tidak ada yang dipilih, kedua
   baris itu tidak ikut terkirim - lihat JadwalDokterContent.jsx.
   ============================================================ */
export const PESAN_WA = {
  id: "Halo RS Jakarta Medical Center,\n\nSaya ingin mendaftar untuk berobat.",
  en: "Hello RS Jakarta Medical Center,\n\nI would like to register for an appointment.",
};

export const PESAN_PENUTUP = {
  id: "Mohon informasi jadwal dan ketersediaannya. Terima kasih.",
  en: "Could you let me know the schedule and availability? Thank you.",
};
